import styled from 'styled-components';
import { getBrandSiteHost } from '../../../utils/siteHost';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { authStore, authActions } from '../../../../store/authStore';
import { roleStore, Role } from '../../../../store/roleStore';
import { useState, useRef } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  UPDATE_USER,
  GET_CURRENT_USER,
  APPLY_TO_BE_HOST,
} from '../../../services/authService/queries';
import { IUser, HostStatus } from '../../../../__generated__/graphql';
import { toast } from 'react-toastify';
import { PhoneVerification } from '../../../components/PhoneVerification';
import { GuestRatingsSection } from '../../../components/ratings';
import { GeneralDriverLicenseVerificationModal } from '../../../components/GeneralDriverLicenseVerificationModal';
import { isDevApiEnv } from '../../../../store/appTimeStore';
import { EnvironmentSwitchSection } from '../../../components/EnvironmentSwitchSection';
import { AppVersionLabel } from '../../../components/AppVersionLabel';
import { ProfilePaymentMethodsRow } from '../PaymentMethodsPage/ProfilePaymentMethodsRow';
import HostAgreementConsentModal, {
  HostAgreementAcceptance,
} from '../../../components/HostAgreementConsentModal';

const MY_GENERAL_VERIFY_DL = gql`
  query MyGeneralVerifyDL {
    myGeneralVerifyDL {
      id
      isVerifiedByAdmin
      expirationDate
      birthDate
      legalFirstName
      legalMiddleName
      legalLastName
      isValid
      submittedAt
      verifiedAt
      rejectionReason
      canBeAdditionalDriver
    }
  }
`;

/** Same rules as Flutter `account_screen.dart` — list row status copy only */
function profileDlStatusText(dl: any): string {
  if (!dl) return 'Not submitted';

  const submittedRaw = dl.submittedAt;
  const submittedStr = submittedRaw ? String(submittedRaw).trim() : '';
  let hasSubmission = submittedStr.length > 0 && submittedStr !== 'null';

  const adminOk = dl.isVerifiedByAdmin === true;
  const validOk = dl.isValid === true;
  const driverOk = dl.canBeAdditionalDriver === true;
  const reason = dl.rejectionReason ? String(dl.rejectionReason).trim() : '';
  const adminRejectedOutcome = adminOk && validOk !== true;

  if (!hasSubmission && !reason && !adminRejectedOutcome) {
    const idStr = dl.id ? String(dl.id).trim() : '';
    const expPresent = dl.expirationDate != null && String(dl.expirationDate).trim() !== '';
    if (idStr.length > 0 && expPresent && !adminOk) {
      hasSubmission = true;
    }
  }

  if (adminOk && validOk && driverOk) {
    const exp = dl.expirationDate ? new Date(dl.expirationDate) : null;
    const tail = exp && !Number.isNaN(exp.getTime()) ? `, expires ${exp.toLocaleDateString()}` : '';
    return `Verified${tail}.`;
  }

  if (reason || adminRejectedOutcome) {
    const base = 'Your driver license verification did not pass verification. Please redo it.';
    return reason ? `${base} Reason: ${reason}` : base;
  }

  if (hasSubmission) {
    return 'Submitted, waiting for admin review. Verification usually takes about 10 minutes.';
  }

  return 'Not submitted';
}

/** Fully verified for additional-driver use (same gate as verified status copy). */
function profileDlIsFullyVerified(dl: any): boolean {
  if (!dl) return false;
  return dl.isVerifiedByAdmin === true && dl.isValid === true && dl.canBeAdditionalDriver === true;
}

const PageContainer = styled.div`
  ${tw`
    flex
    flex-col
    w-full
    max-w-full
    min-w-0
    h-full
    items-center
    overflow-x-hidden
    box-border
  `}
`;

const ProfileContainer = styled.div`
  ${tw`w-full max-w-md min-w-0 mx-auto px-4 pt-6 pb-10 md:pt-10 box-border`}
`;

const UserInfoItem = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    mb-4
    box-border
  `}
`;

const Label = styled.p`
  ${tw`
    text-sm
    text-text-secondary
    mb-1
  `}
`;

const Value = styled.p`
  ${tw`
    text-lg
    font-semibold
    text-text-primary
  `}
`;

const PhoneStatus = styled.div<{ verified?: boolean }>`
  ${tw`
    inline-flex
    items-center
    px-2
    py-1
    rounded-full
    text-xs
    font-medium
    mt-1
 max-w-full min-w-0 box-border`}
  ${({ verified }) =>
    verified ? tw`bg-green-100 text-green-800` : tw`bg-orange-100 text-orange-800 cursor-pointer`}
`;

const ChangePhoneButton = styled.button`
  ${tw`
    text-sm
    font-medium
    text-black
    ml-2
    border-0
    bg-transparent
    cursor-pointer
  `}
  &:hover {
    ${tw`text-indigo-800`}
  }
`;

const StyledButton = styled.button`
  ${tw`w-full mt-3 py-2.5 bg-black text-white rounded-lg text-sm font-semibold shadow-xs border-none cursor-pointer`}
  &:hover {
    ${tw`bg-black-hover`}
  }
`;

const InputContainer = styled.div`
  width: 320px;
  ${tw`w-full mb-4`}
`;

const InputLabel = styled.div`
  ${tw`
    text-sm
    text-text-secondary
    mb-1
  `}
`;

const StyledInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  ${tw`
    p-2
    border
    border-border-default
    rounded-md
  `}
`;

const StyledSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  ${tw`
    p-2
    border
    border-border-default
    rounded-md
  `}
`;

const HostStatusBadge = styled.span<{ status: 'approved' | 'pending' | 'rejected' }>`
  ${tw`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium`}
  ${({ status }) => {
    switch (status) {
      case 'approved':
        return tw`bg-green-100 text-green-800`;
      case 'pending':
        return tw`bg-yellow-100 text-yellow-800`;
      case 'rejected':
        return tw`bg-red-100 text-red-800`;
    }
  }}
`;

const ApplyHostButton = styled(StyledButton)`
  &:disabled {
    ${tw`bg-bg-secondary text-text-tertiary shadow-none cursor-not-allowed`}
  }
  &:disabled:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const DevLinkButton = styled.button`
  ${tw`w-full mt-3 py-2.5 border border-gray-800 rounded-lg text-sm font-medium text-text-primary bg-white`}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const DLPanel = styled.div`
  ${tw`mt-4 p-4 rounded-lg border border-green-200 bg-green-50`}
`;

const DLStatus = styled.div`
  ${tw`text-sm text-text-primary mb-3`}
`;

const DLOpenButton = styled.button`
  ${tw`w-full mt-3 py-2.5 px-4 rounded-lg font-medium text-sm bg-white text-sky-950 hover:bg-sky-300`}
`;

export function ProfilePage() {
  const [isEditing, setIsEditing] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showHostAgreement, setShowHostAgreement] = useState(false);
  const { user } = useSnapshot(authStore);
  const { role } = useSnapshot(roleStore);

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isAdmin, setIsAdmin] = useState(user?.isAdmin || false);
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [isDealer, setIsDealer] = useState(user?.isDealer || false);
  const [showDlModal, setShowDlModal] = useState(false);

  const [updateUser, { loading, error }] = useMutation(UPDATE_USER, {
    refetchQueries: [{ query: GET_CURRENT_USER }],
  });

  const [applyToBeHost, { loading: applyingHost }] = useMutation(APPLY_TO_BE_HOST, {
    refetchQueries: [{ query: GET_CURRENT_USER }],
  });

  const { data: generalDLData, refetch: refetchGeneralDL } = useQuery(MY_GENERAL_VERIFY_DL, {
    fetchPolicy: 'cache-and-network',
  });

  const pendingHostAfterPhoneVerification = useRef(false);
  /** The Host Agreement accepted in the dialog; sent with the application. */
  const hostAgreementAcceptance = useRef<HostAgreementAcceptance | null>(null);

  const navigate = useNavigate();

  const handleLogout = async () => {
    await authActions.logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleSave = async () => {
    try {
      await updateUser({
        variables: {
          updateUserInput: {
            displayName,
            isDealer,
            firstName,
            lastName,
          },
        },
      });
      authActions.setUser({
        ...user!,
        displayName,
        isAdmin,
        isDealer,
        firstName,
        lastName,
      } as Partial<IUser>);
      setIsEditing(false);
    } catch (error) {}
  };

  const submitHostApplication = async () => {
    try {
      // D36: the application names the brand it is made on.
      // The server records the accepted Host Agreement version before the application.
      const { data } = await applyToBeHost({
        variables: {
          siteUrl: getBrandSiteHost(),
          hostAgreement: hostAgreementAcceptance.current,
        },
      });
      if (data?.applyToBeHost) {
        authActions.setUser({
          ...user!,
          hostStatus: data.applyToBeHost.hostStatus,
          isHost: data.applyToBeHost.isHost,
        } as Partial<IUser>);
        toast.success('Host application submitted successfully!');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit host application');
    }
  };

  /** Every application starts with the brand's Host Agreement (spec: Lenvoy policies). */
  const handleApplyToBeHost = () => {
    setShowHostAgreement(true);
  };

  const handleHostAgreementAccepted = async (acceptance: HostAgreementAcceptance) => {
    hostAgreementAcceptance.current = acceptance;
    setShowHostAgreement(false);
    if (!user?.isPhoneVerified) {
      pendingHostAfterPhoneVerification.current = true;
      toast.info('To become a host, you must have a phone number on record');
      setShowPhoneModal(true);
      return;
    }

    await submitHostApplication();
  };

  const handlePhoneVerified = async () => {
    if (pendingHostAfterPhoneVerification.current) {
      pendingHostAfterPhoneVerification.current = false;
      await submitHostApplication();
    }
  };

  const generalDL = generalDLData?.myGeneralVerifyDL;
  const dlIsFullyVerified = profileDlIsFullyVerified(generalDL);
  const dlOpenButtonLabel = dlIsFullyVerified
    ? 'Update Driver License'
    : 'Driver license verification';

  const dlStatusText = profileDlStatusText(generalDL);

  if (!user) {
    navigate('/');
    return null;
  }

  return (
    <PageContainer>
      <ProfileContainer>
        {isEditing ? (
          <>
            <InputContainer>
              <InputLabel>Email:</InputLabel>
              <StyledInput value={user?.email} disabled />
            </InputContainer>
            <InputContainer>
              <InputLabel>Display Name:</InputLabel>
              <StyledInput value={displayName} onChange={e => setDisplayName(e.target.value)} />
            </InputContainer>
            <InputContainer>
              <InputLabel>First Name:</InputLabel>
              <StyledInput value={firstName} onChange={e => setFirstName(e.target.value)} />
            </InputContainer>
            <InputContainer>
              <InputLabel>Last Name:</InputLabel>
              <StyledInput value={lastName} onChange={e => setLastName(e.target.value)} />
            </InputContainer>
            <InputContainer>
              <InputLabel>Admin Status:</InputLabel>
              <StyledSelect
                value={isAdmin.toString()}
                onChange={e => setIsAdmin(e.target.value === 'true')}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </StyledSelect>
            </InputContainer>
            {user.isDealer && (
              <InputContainer>
                <InputLabel>Dealer Status:</InputLabel>
                <StyledSelect
                  value={isDealer.toString()}
                  onChange={e => setIsDealer(e.target.value === 'true')}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </StyledSelect>
              </InputContainer>
            )}
            <StyledButton onClick={handleSave}>Save</StyledButton>
          </>
        ) : (
          <>
            <UserInfoItem>
              <Label>Email</Label>
              <Value>{user.email}</Value>
            </UserInfoItem>
            <UserInfoItem hidden={!user.firstName || !user.lastName}>
              <Label>Name</Label>
              <Value>{user.firstName + ' ' + user.lastName}</Value>
            </UserInfoItem>
            <UserInfoItem>
              <Label>Display Name</Label>
              <Value>{user.displayName ?? user.email}</Value>
            </UserInfoItem>
            <UserInfoItem>
              <Label>Host Status</Label>
              <Value>
                {user.hostStatus === HostStatus.Approved && (
                  <HostStatusBadge status="approved">Approved Host</HostStatusBadge>
                )}
                {user.hostStatus === HostStatus.Pending && (
                  <HostStatusBadge status="pending">Application Pending</HostStatusBadge>
                )}
                {user.hostStatus === HostStatus.Rejected && (
                  <HostStatusBadge status="rejected">Application Rejected</HostStatusBadge>
                )}
                {(user.hostStatus === HostStatus.NotHost || !user.hostStatus) && (
                  <span tw="text-sm text-text-secondary">Not a host</span>
                )}
              </Value>
            </UserInfoItem>
            {user.isDealer && (
              <UserInfoItem>
                <Label>I am a Dealer</Label>
                <Value>Yes</Value>
              </UserInfoItem>
            )}
            <UserInfoItem>
              <Label>Phone Number</Label>
              <Value>
                {user.phoneNumber || 'Not set'}
                {!user.isPhoneVerified && (
                  <PhoneStatus onClick={() => setShowPhoneModal(true)}>
                    {user.phoneNumber ? 'Unverified - Verify Now' : 'Add Phone Number'}
                  </PhoneStatus>
                )}
                {user.isPhoneVerified && (
                  <>
                    <PhoneStatus verified>Verified</PhoneStatus>
                    <ChangePhoneButton onClick={() => setShowPhoneModal(true)}>
                      Change
                    </ChangePhoneButton>
                  </>
                )}
              </Value>
            </UserInfoItem>
            {role === Role.GUEST && (
              <UserInfoItem>
                <ProfilePaymentMethodsRow />
              </UserInfoItem>
            )}
            {isDevApiEnv() && (
              <UserInfoItem>
                <Label>Developer</Label>
                <DevLinkButton type="button" onClick={() => navigate('/dev/datetime')}>
                  Mock time
                </DevLinkButton>
              </UserInfoItem>
            )}
            <UserInfoItem>
              <Label>Driving</Label>
              <DLPanel>
                <DLStatus>
                  <strong>Driver license verification:</strong> {dlStatusText}
                  {!generalDL?.isVerifiedByAdmin && generalDL && (
                    <div>Verification usually takes about 10 minutes after review starts.</div>
                  )}
                  {generalDL &&
                    [
                      generalDL.legalFirstName,
                      generalDL.legalMiddleName,
                      generalDL.legalLastName,
                    ].some(Boolean) && (
                      <div tw="mt-1 text-xs text-text-secondary">
                        Legal name on file:{' '}
                        {[
                          generalDL.legalFirstName,
                          generalDL.legalMiddleName,
                          generalDL.legalLastName,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </div>
                    )}
                </DLStatus>
                <DLOpenButton type="button" onClick={() => setShowDlModal(true)}>
                  {dlOpenButtonLabel}
                </DLOpenButton>
              </DLPanel>
            </UserInfoItem>
            {(user.hostStatus === HostStatus.Rejected ||
              user.hostStatus === HostStatus.NotHost ||
              !user.hostStatus) && (
              <ApplyHostButton onClick={handleApplyToBeHost} disabled={applyingHost}>
                {applyingHost
                  ? 'Submitting...'
                  : user.hostStatus === HostStatus.Rejected
                    ? 'Re-apply'
                    : 'Apply to be a Host'}
              </ApplyHostButton>
            )}
            <EnvironmentSwitchSection />
            <StyledButton onClick={() => setIsEditing(true)}>Edit Profile</StyledButton>
            <StyledButton onClick={handleLogout}>Sign out</StyledButton>
          </>
        )}
      </ProfileContainer>
      <HostAgreementConsentModal
        isOpen={showHostAgreement}
        onClose={() => setShowHostAgreement(false)}
        onAgree={handleHostAgreementAccepted}
      />
      {showPhoneModal && (
        <PhoneVerification
          onClose={() => {
            setShowPhoneModal(false);
            pendingHostAfterPhoneVerification.current = false;
          }}
          onVerified={handlePhoneVerified}
          initialPhoneNumber={user.phoneNumber || ''}
        />
      )}

      <GeneralDriverLicenseVerificationModal
        isOpen={showDlModal}
        onClose={() => setShowDlModal(false)}
        onAfterSubmit={() => refetchGeneralDL()}
      />

      {/* Reviews from hosts (as a guest) */}
      {user?.id && (
        <ProfileContainer>
          <GuestRatingsSection guestId={user.id} />
        </ProfileContainer>
      )}

      <AppVersionLabel variant="page" />
    </PageContainer>
  );
}

export default ProfilePage;
